import { FC, useEffect, useState } from 'react';
import { useStyles } from './styles';
import { useStore } from 'hooks/useRootStore';
import { useForm, Controller } from 'react-hook-form';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useQuery, useMutation } from 'react-query';
import { Grid, Input, IconButton, InputAdornment, InputLabel, FormControl, FormHelperText } from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import Button from '@material-ui/core/Button';
import { observer } from 'mobx-react-lite';
import { AdminPasswordInfo } from 'domain/user/types';

export const SecondaryUserPasswordForm: FC = observer(() => {
  const classes = useStyles();
  const [passLowerCase, setPassLowerCase] = useState<number>(0);
  const [passUpperCase, setPassUpperCase] = useState<number>(0);
  const [passNumericCase, setPassNumericCase] = useState<number>(0);
  const [passSpecialCase, setPassSpecialCase] = useState<number>(0);
  const [minPass, SetMinPass] = useState<number>(0);
  const [showoldPassword, setShowOldPassword] = useState<boolean>(false);
  const { control, handleSubmit, watch, reset } = useForm<AdminPasswordInfo>();
  const {
    userStore: { updateEndUserPasswordInfo },
    endUserSettingsFormStore: { getSettings, settingsByTypeView },
    notificationStore: { openNotification },
  } = useStore();
  const updateUserProfileInfoReq = useMutation((data: AdminPasswordInfo) => updateEndUserPasswordInfo(data), {
    onSuccess: () => {
      openNotification('success', `Saved!!`);
      reset({
        current_password: '',
        password: '',
        password_confirmation: '',
      });
    },
    onError: ({ message }: Error) => openNotification('error', message),
  });
  const getSettingsReq = useQuery('getSettingReq', () => getSettings(), {
    onError: () => openNotification('error', `Can't get password policy`),
  });
  useEffect(() => {
    settingsByTypeView('password').forEach((group: any, i: any) =>
      group.settings.forEach((setting: any, j: any) => {
        if (setting.key === 'minimumNumberOfLowercaseCharacters') {
          return setPassLowerCase(+setting.value);
        } else if (setting.key === 'minimumNumberOfUppercaseCharacters') {
          return setPassUpperCase(+setting.value);
        } else if (setting.key === 'minimumNumberOfNumericCharacters') {
          return setPassNumericCase(+setting.value);
        } else if (setting.key === 'minimumNumberOfSpecialCharacters') {
          return setPassSpecialCase(+setting.value);
        } else if (setting.key === 'minimumLengthPassword') {
          return SetMinPass(+setting.value);
        } else {
          return 0;
        }
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSettingsReq.isFetching]);
  const onSubmit = (data: AdminPasswordInfo) => {
    return updateUserProfileInfoReq.mutate(data);
  };
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  useAppPreloader(getSettingsReq.isFetching || updateUserProfileInfoReq.isLoading);
  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <Grid className={classes.btnContainer}>
        <Button className={classes.btn} type='submit' variant='contained'>
          Save Changes
        </Button>
      </Grid>
      <div className={classes.formContainer}>
        <div className={classes.rightCol}>
          <div className={classes.heading}>Change Password</div>
          <Grid container justify='space-between' className={classes.container}>
            <Grid className={classes.block} container>
              <Controller
                name='current_password'
                control={control}
                rules={{ required: false }}
                render={({ field, fieldState: { invalid } }) => (
                  <Grid className={classes.row} container>
                    <FormControl className={classes.input}>
                      <InputLabel htmlFor='password'>Current Password</InputLabel>
                      <Input
                        {...field}
                        id='current_password'
                        name='current_password'
                        onChange={field.onChange}
                        error={invalid}
                        type={showoldPassword ? 'text' : 'password'}
                        endAdornment={
                          <InputAdornment position='end'>
                            <IconButton
                              aria-label='toggle password visibility'
                              onClick={() => setShowOldPassword(!showoldPassword)}
                            >
                              {showoldPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        }
                        key='current_password'
                        placeholder='Enter Current Password'
                      />
                    </FormControl>
                  </Grid>
                )}
              />
            </Grid>
          </Grid>
          <Grid container justify='space-between' className={classes.container}>
            <Grid className={classes.block} container>
              <Controller
                name='password'
                control={control}
                rules={{
                  required: false,
                  pattern: {
                    value: new RegExp(
                      `^(?=(?:.*[A-Z]){${passUpperCase},})(?=(?:.*[a-z]){${passLowerCase},})(?=(?:.*\\d){${passNumericCase},})(?=(?:.*[!@#$%^&*()_+={}[]|\\:;"'<>,.?/~\`-]){${passSpecialCase},}).{${minPass},}$`
                    ),
                    message: `Password must contain at least ${passUpperCase} uppercase, ${passLowerCase} lowercase, ${passSpecialCase} special characters, ${passNumericCase} numeric value(s), and minimum ${minPass} characters.`,
                  },
                }}
                render={({ field, fieldState }) => (
                  <Grid className={classes.row} container>
                    <FormControl className={classes.input} error={fieldState.invalid}>
                      <InputLabel htmlFor='password'>New Password</InputLabel>
                      <Input
                        {...field}
                        id='password'
                        name='password'
                        onChange={field.onChange}
                        error={fieldState.invalid}
                        type={showPassword ? 'text' : 'password'}
                        endAdornment={
                          <InputAdornment position='end'>
                            <IconButton
                              aria-label='toggle password visibility'
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        }
                        placeholder='Enter New Password'
                      />
                      {fieldState.invalid && (
                        <FormHelperText style={{ color: 'red', fontSize: '12px' }}>
                          {fieldState.error?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              />
              <Controller
                name='password_confirmation'
                control={control}
                rules={{
                  required: false,
                  validate: (value) => value === watch('password') || 'Passwords do not match',
                }}
                render={({ field, fieldState }) => (
                  <Grid className={classes.row} container>
                    <FormControl className={classes.input} error={fieldState.invalid}>
                      <InputLabel htmlFor='confirmpassword'>Confirm New Password</InputLabel>
                      <Input
                        id='confirmpassword'
                        {...field}
                        onChange={field.onChange}
                        error={fieldState.invalid}
                        type={showConfirmPassword ? 'text' : 'password'}
                        key='confirmpassword'
                        endAdornment={
                          <InputAdornment position='end'>
                            <IconButton
                              aria-label='toggle password visibility'
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        }
                        placeholder='Confirm New Password'
                      />
                      {fieldState.invalid && (
                        <FormHelperText style={{ color: 'red', fontSize: '12px' }}>
                          {fieldState.error?.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    </form>
  );
});
